import { NavLink } from "react-router-dom";

import { LeagueBadge } from "@/components/triumvirate/LeagueBadge";

export const TRIUMVIRATE_MODE = { id: "overall", label: "Live Ladder" };
export const LANES = ["left", "center", "right"];

export function deltaText(value) {
  const amount = Number(value || 0);
  return `${amount > 0 ? "+" : ""}${amount}`;
}

export function formatDate(value) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

export function getUnifiedRatingBlock(source) {
  return source?.overall || { rating: 1000, wins: 0, losses: 0 };
}

export function RuleCard({ testId, title, body }) {
  return (
    <div data-testid={testId} className="border border-amber-900/35 bg-zinc-950/70 p-4 rounded-sm">
      <div className="text-sm font-cinzel uppercase tracking-[0.16em] text-amber-300">{title}</div>
      <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{body}</p>
    </div>
  );
}

export function TriumvirateSectionNav() {
  const items = [
    { id: "builder", label: "Squad Builder", to: "/ludus-trias", end: true },
    { id: "opponents", label: "Opponents", to: "/ludus-trias/opponents", end: false },
    { id: "matches", label: "Matches", to: "/ludus-trias/matches", end: false },
    { id: "ladder", label: "Ladder", to: "/ludus-trias/ladder", end: false },
  ];

  return (
    <section data-testid="triumvirate-section-nav" className="flex flex-wrap gap-2">
      {items.map((item) => (
        <NavLink
          key={item.id}
          to={item.to}
          end={item.end}
          data-testid={`triumvirate-section-nav-${item.id}`}
          className={({ isActive }) =>
            `px-4 py-2 text-sm font-cinzel uppercase tracking-[0.16em] rounded-sm border transition-colors ${
              isActive
                ? "border-amber-500 bg-amber-950/25 text-amber-200"
                : "border-zinc-800 bg-zinc-950/65 text-zinc-400 hover:border-amber-700/60 hover:text-amber-200"
            }`
          }
        >
          {item.label}
        </NavLink>
      ))}
    </section>
  );
}

export function TriumvirateHeroPanel({ pageKey = "triumvirate", title, copy, ratings, careerRatings, season }) {
  const rating = getUnifiedRatingBlock(ratings);
  const career = getUnifiedRatingBlock(careerRatings);

  return (
    <section
      data-testid={`${pageKey}-hero`}
      className="relative overflow-hidden border border-amber-900/35 rounded-sm bg-[radial-gradient(circle_at_top_left,_rgba(251,191,36,0.14),_transparent_34%),linear-gradient(135deg,rgba(24,24,27,0.96),rgba(9,9,11,0.98))] p-6 sm:p-8"
    >
      <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr] xl:items-end">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-amber-300">Ranked 3v3 · Monthly season</div>
          <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-cinzel text-amber-100 leading-[0.95] max-w-3xl">{title}</h2>
          <p data-testid={`${pageKey}-hero-copy`} className="mt-4 max-w-2xl text-sm sm:text-base text-zinc-300 leading-relaxed">
            {copy}
          </p>
        </div>

        <div data-testid={`${pageKey}-current-summary`} className="border border-amber-700/35 bg-zinc-950/70 p-5 rounded-sm space-y-4">
          <div>
            <div className="text-xs uppercase tracking-[0.16em] text-zinc-500">Mode</div>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
              <LeagueBadge league={TRIUMVIRATE_MODE.id} label={TRIUMVIRATE_MODE.label} testId={`${pageKey}-mode-badge`} />
              <div className="text-right">
                <div data-testid={`${pageKey}-current-rating`} className="font-cinzel text-3xl text-amber-300 tabular-nums">
                  {Number(rating?.rating || 1000).toLocaleString()}
                </div>
                <div className="text-xs text-zinc-500">season rating</div>
              </div>
            </div>
            <div data-testid={`${pageKey}-current-record`} className="mt-3 text-sm text-zinc-400">
              {rating?.wins || 0} wins · {rating?.losses || 0} losses · Career {Number(career?.rating || 1000).toLocaleString()}
            </div>
          </div>

          <div data-testid={`${pageKey}-season-card`} className="border border-zinc-800 bg-zinc-950/50 rounded-sm p-4">
            <div className="text-xs uppercase tracking-[0.16em] text-zinc-500">Season</div>
            <div data-testid={`${pageKey}-season-label`} className="mt-2 font-cinzel text-xl text-amber-200">{season?.label || "Current season"}</div>
            <div className="mt-1 text-sm text-zinc-500">Ends {formatDate(season?.ends_at)}</div>
            <div data-testid={`${pageKey}-season-remaining`} className="mt-3 text-sm text-zinc-300">{season?.days_remaining || 0} day(s) remaining</div>
          </div>
        </div>
      </div>
    </section>
  );
}